import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Slide,
  useTheme,
  alpha
} from '@mui/material';
import { 
  CheckCircle, 
  Error, 
  Warning, 
  Info, 
  Close,
  Notifications,
  NotificationsActive
} from '@mui/icons-material';

const FeedbackContext = createContext();

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};

// Transition pour les snackbars
const SlideTransition = (props) => {
  return <Slide {...props} direction="left" />;
};

export const FeedbackProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
    duration: 6000,
    action: null
  });
  const [loading, setLoading] = useState({
    open: false,
    message: 'Chargement...'
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    severity: 'warning',
    onConfirm: null,
    onCancel: null
  });
  const [notifications, setNotifications] = useState([]);
  const theme = useTheme();

  const showSnackbar = useCallback((message, severity = 'info', options = {}) => {
    setSnackbar({
      open: true,
      message,
      severity,
      duration: options.duration || 6000,
      action: options.action || null
    });
  }, []);

  const showSuccess = useCallback((message, options = {}) => 
    showSnackbar(message, 'success', options), [showSnackbar]);
  
  const showError = useCallback((message, options = {}) => 
    showSnackbar(message, 'error', options), [showSnackbar]);
  
  const showWarning = useCallback((message, options = {}) => 
    showSnackbar(message, 'warning', options), [showSnackbar]);
  
  const showInfo = useCallback((message, options = {}) => 
    showSnackbar(message, 'info', options), [showSnackbar]);

  const showLoading = useCallback((isLoading, message = 'Chargement...') => {
    setLoading({
      open: isLoading,
      message
    });
  }, []);

  const showConfirm = useCallback((title, message, onConfirm, options = {}) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      confirmText: options.confirmText || 'Confirmer',
      cancelText: options.cancelText || 'Annuler',
      severity: options.severity || 'warning',
      onConfirm: () => {
        onConfirm();
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
      onCancel: () => {
        if (options.onCancel) options.onCancel();
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, ...notification }]);
    
    // Auto-remove after duration
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, notification.duration || 5000);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const getIcon = (severity) => {
    switch (severity) {
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <Error />;
      case 'warning':
        return <Warning />;
      case 'info':
      default:
        return <Info />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
      default:
        return theme.palette.info.main;
    }
  };

  return (
    <FeedbackContext.Provider
      value={{
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showLoading,
        showConfirm,
        addNotification,
        removeNotification
      }}
    >
      {children}

      {/* Snackbar amélioré pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={SlideTransition}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          icon={getIcon(snackbar.severity)}
          action={snackbar.action}
          sx={{ 
            width: '100%',
            minWidth: 300,
            '& .MuiAlert-message': {
              fontSize: '0.875rem',
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Backdrop de chargement amélioré */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(4px)'
        }}
        open={loading.open}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="inherit" size={48} />
          <Typography variant="h6" color="inherit">
            {loading.message}
          </Typography>
        </Box>
      </Backdrop>

      {/* Dialog de confirmation amélioré */}
      <Dialog
        open={confirmDialog.open}
        onClose={confirmDialog.onCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[8]
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 1
        }}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              backgroundColor: alpha(getSeverityColor(confirmDialog.severity), 0.1),
              color: getSeverityColor(confirmDialog.severity),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {getIcon(confirmDialog.severity)}
          </Box>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={confirmDialog.onCancel}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {confirmDialog.cancelText}
          </Button>
          <Button 
            onClick={confirmDialog.onConfirm} 
            variant="contained" 
            color={confirmDialog.severity}
            sx={{ borderRadius: 2 }}
          >
            {confirmDialog.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications persistantes */}
      <Box
        sx={{
          position: 'fixed',
          top: 80,
          right: 16,
          zIndex: theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 400
        }}
      >
        {notifications.map((notification) => (
          <Alert
            key={notification.id}
            severity={notification.severity || 'info'}
            icon={getIcon(notification.severity || 'info')}
            action={
              <IconButton
                size="small"
                onClick={() => removeNotification(notification.id)}
                color="inherit"
              >
                <Close fontSize="small" />
              </IconButton>
            }
            sx={{
              boxShadow: theme.shadows[4],
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '0.875rem'
              }
            }}
          >
            {notification.message}
          </Alert>
        ))}
      </Box>
    </FeedbackContext.Provider>
  );
};
